import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

function VoiceRow({ v, i, bases, set, remove }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-[1.6fr_1.4fr_1fr_0.9fr_auto_auto] gap-2 items-center p-3" data-testid={`price-voice-${i}`}>
      <Input value={v.label} onChange={(e) => set(i, { label: e.target.value })} placeholder="Voce" className="col-span-2 sm:col-span-1" data-testid={`price-voice-label-${i}`} />
      <select value={v.basis} onChange={(e) => set(i, { basis: e.target.value })} className="h-10 rounded-md border border-slate-200 px-2 text-sm bg-white" data-testid={`price-voice-basis-${i}`}>
        {Object.entries(bases).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <Input value={v.unit} onChange={(e) => set(i, { unit: e.target.value })} placeholder="Unità (es. ora)" data-testid={`price-voice-unit-${i}`} />
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
        <Input type="number" min="0" step="0.01" value={v.price} onChange={(e) => set(i, { price: e.target.value })} className="pl-7" data-testid={`price-voice-price-${i}`} />
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-600">
        <Switch checked={v.enabled} onCheckedChange={(c) => set(i, { enabled: c })} data-testid={`price-voice-enabled-${i}`} /> Inclusa
      </label>
      <button onClick={() => remove(i)} className="justify-self-end p-2 text-slate-400 hover:text-rose-600 transition-colors" aria-label="Elimina voce" data-testid={`price-voice-delete-${i}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function PriceList() {
  const [pl, setPl] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/price-list").then((r) => setPl(r.data)); }, []);
  if (!pl) return null;
  const set = (i, patch) => setPl({ ...pl, voices: pl.voices.map((v, j) => (j === i ? { ...v, ...patch } : v)) });
  const remove = (i) => setPl({ ...pl, voices: pl.voices.filter((_, j) => j !== i) });
  const add = () => setPl({ ...pl, voices: [...pl.voices, { label: "", unit: "", basis: "fixed", price: 0, enabled: true }] });
  const save = async () => {
    setSaving(true);
    try {
      const body = { vat_rate: Number(pl.vat_rate) || 0, validity_days: Number(pl.validity_days) || 30, voices: pl.voices.map((v) => ({ ...v, price: Number(v.price) || 0 })) };
      const { data } = await api.put("/price-list", body);
      setPl(data);
      toast.success("Listino salvato: vale per i nuovi preventivi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const missing = pl.voices.filter((v) => v.enabled && !(Number(v.price) > 0)).length;
  return (
    <div className="space-y-6" data-testid="price-list-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Preventivi</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Listino costi</h1>
        <p className="text-slate-500 mt-2 text-sm">I tuoi prezzi. ChatGPT stima solo le quantità (ore, furgoni, km, scatoloni); i prezzi li decidi qui e li correggi su ogni preventivo.</p>
      </header>
      {missing > 0 && <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-xl p-3" data-testid="price-list-missing-warning">{missing} voci incluse hanno ancora prezzo 0 €: inseriscili per avere preventivi completi.</div>}
      <section className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
        {pl.voices.map((v, i) => <VoiceRow key={v.key || `new-${i}`} v={v} i={i} bases={pl.bases} set={set} remove={remove} />)}
        <div className="p-3"><Button variant="outline" onClick={add} data-testid="price-voice-add-button"><Plus className="w-4 h-4 mr-2" /> Aggiungi voce</Button></div>
      </section>
      <section className="bg-white rounded-2xl border border-slate-200 p-4 grid sm:grid-cols-2 gap-4">
        <label className="text-sm font-medium text-slate-700">IVA (%)
          <Input type="number" min="0" max="50" value={pl.vat_rate} onChange={(e) => setPl({ ...pl, vat_rate: e.target.value })} className="mt-1" data-testid="price-list-vat-input" />
        </label>
        <label className="text-sm font-medium text-slate-700">Validità del preventivo (giorni)
          <Input type="number" min="1" max="365" value={pl.validity_days} onChange={(e) => setPl({ ...pl, validity_days: e.target.value })} className="mt-1" data-testid="price-list-validity-input" />
        </label>
      </section>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="price-list-save-button"><Save className="w-4 h-4 mr-2" /> Salva listino</Button>
      </div>
    </div>
  );
}
